"use client";

import { ArrowRight, Check, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const expectations = ["Pro-player roster recruitment review", "Tournament schedule integration", "Community Discord access credentials"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary/40 bg-primary/5">
        <p className="font-default text-lg text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div>
        <p className="font-default text-[11px] uppercase tracking-[0.2em] text-primary mb-6">
          Section A — Player Profile
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Gamer Tag
                </FormLabel>
                <FormControl>
                  <Input placeholder="e.g. ForceOne" className="bg-transparent border-0 border-b border-muted px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email_address" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Contact Email
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="player@pixelforce.gg" className="bg-transparent border-0 border-b border-muted px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Discord ID
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Username#0000" className="bg-transparent border-0 border-b border-muted px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="preferred_date" rules={{
          required: "Preferred date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Tournament Date
                </FormLabel>
                <FormControl>
                  <Input type="date" className="bg-transparent border-0 border-b border-muted px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary [color-scheme:dark]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div>
        <p className="font-default text-[11px] uppercase tracking-[0.2em] text-primary mb-6">
          Section B — Competitive Focus
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="dog_breed" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Primary Game Title
                </FormLabel>
                <FormControl>
                  <Input placeholder="Valorant, CS2, Apex, etc." className="bg-transparent border-0 border-b border-muted px-0 h-10 text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="service_interest" rules={{
          required: "Please select a category"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Membership Tier
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-muted px-0 h-10 text-base font-default text-foreground focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary w-full">
                      <SelectValue placeholder="Select Tier" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="Casual">Casual Community</SelectItem>
                      <SelectItem value="Competitive">Competitive Roster</SelectItem>
                      <SelectItem value="Pro">Pro-Circuit Elite</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="mt-6">
          <FormField control={control} name="additional_notes" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Player Bio / Experience
                </FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us about your competitive history..." className="bg-transparent border-0 border-b border-muted px-0 min-h-[120px] text-base font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary resize-none" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Checkbox id="journal-subscribe" className="border-muted-foreground rounded-lg" />
        <label htmlFor="journal-subscribe" className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">
          Join the Pixel Force Newsletter
        </label>
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default text-sm uppercase tracking-[0.2em] font-semibold shadow-2xl" style={{
      boxShadow: "0 20px 60px -10px hsla(180, 100%, 50%, 0.4)"
    }}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Transmitting...
          </> : <>
            Submit Application
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function TacticalSupport() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3963" id="tactical-support" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-lg opacity-[0.08] blur-[120px] pointer-events-none" style={{
      backgroundColor: "var(--primary)"
    }} aria-hidden="true" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-[11px] uppercase tracking-[0.3em] text-primary mb-8">
              03 — Recruitment
            </p>

            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-10">
              Join the
              <br />
              Pixel Force
              <span className="text-primary">.</span>
            </h2>

            <p className="font-default text-lg leading-relaxed text-muted-foreground mb-12 max-w-md">
              Every elite squad starts with a single connection. Submit your credentials to our command center, and our scouts will review your performance data to determine your fit within our competitive rosters.
            </p>

            <div>
              <p className="font-default text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
                Application Requirements
              </p>
              <ul className="space-y-4">
                {expectations.map((item, i) => <li data-index={i} key={i} className="flex items-start gap-4">
                    <Check className="size-4 text-primary mt-1 shrink-0" strokeWidth={2.5} />
                    <span className="font-default text-sm uppercase tracking-[0.15em] text-foreground">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-16 pt-8 border-t border-border">
              <p className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                Command Center
              </p>
              <p className="font-default text-base text-foreground">+1 (555) 000-FORCE</p>
              <p className="font-default text-base text-foreground">ops@pixelforce.gg</p>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:pl-8 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="tactical_support_form_the_grid" formId="tactical_support_form_the_grid" className="relative p-8 md:p-14 lg:p-[60px] border shadow-4xl" style={{
            backgroundColor: "hsla(0, 0%, 100%, 0.04)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            borderColor: "hsla(0, 0%, 100%, 0.08)"
          }} sectionName="tactical-support" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            preferred_date: "",
            service_interest: "",
            dog_breed: "",
            additional_notes: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true
            },
            preferred_date: {
              required: true
            },
            service_interest: {
              required: true
            }
          }} submitText="Submit Application" successMessage="Application received. Our scouts are reviewing your data and will contact you via Discord within 48 hours." errorMessage="Transmission failed. Please check your connection and try again.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}